'use client';

import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp, Settings } from 'lucide-react';
import { type ReactNode, useState } from 'react';

interface CollapsibleSectionProps {
  title: string | ReactNode;
  icon?: ReactNode;
  children: ReactNode;
  disabled?: boolean;
  defaultOpen?: boolean;
  className?: string;
  contentClassName?: string;
  buttonClassName?: string;
}

export function CollapsibleSection({
  title,
  icon = <Settings className="w-4 h-4" />,
  children,
  disabled = false,
  defaultOpen = false,
  className,
  contentClassName,
  buttonClassName,
}: CollapsibleSectionProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className={cn('space-y-3', className)}>
      <Button
        type="button"
        variant="ghost"
        size="sm"
        onClick={() => setIsOpen(!isOpen)}
        className={cn(
          'flex items-center justify-center space-x-2 w-full p-3.5 rounded-lg transition-all duration-300',
          'bg-gray-900/40 hover:bg-gray-900/60 border border-green-500/20 hover:border-green-400/30',
          'text-white hover:text-green-400',
          'shadow-sm hover:shadow-md hover:shadow-green-500/10',
          buttonClassName
        )}
        disabled={disabled}
      >
        <div className="text-green-400">{icon}</div>
        <span className="veo3-font-optimized text-sm font-medium flex-1 text-left">{title}</span>
        {isOpen ? (
          <ChevronUp className="w-4 h-4 text-green-400" />
        ) : (
          <ChevronDown className="w-4 h-4 text-green-400" />
        )}
      </Button>
        <div
          className={cn(
            'space-y-5 p-6 rounded-lg border transition-all duration-300',
            'bg-gray-800/30 backdrop-blur-sm border-green-500/15',
            contentClassName,
            isOpen ? 'block' : 'hidden'
          )}
        >
          {children}
        </div>
    </div>
  );
}
